<template>
  <div v-if="!isLoading" class="entity-form">
    <ViewHeader :title="getTitle()">
      <CButton
        v-if="!canEdit"
        color="primary"
        class="ra-button"
        :disabled="!canSaveForm"
        @click="saveForm"
      >
        <Icon name="plus" class="mr-1" v-if="editId === 'create'"/>
        <Icon name="save" class="mr-1" v-else/>
        {{ getTitle() }}
      </CButton>
    </ViewHeader>

    <CRow>
      <CCol md="8">
        <InputComponent
          v-for="field in Object.values(formFields)"
          class="mb-3 w-50"
          :disabled="canEdit || field.disabled"
          :key="field.name"
          :formField="field"
          @select="selectHandler"
        />
      </CCol>
      <CCol md="4">
        <div>
          <div
            v-for="field in autoFields"
            class="mt-2 gap-2 auto-form-wrapper"
          >
            {{ field.label }}
            <RaInput
              size="sm"
              class="filter-input"
              v-model="field.value"
              :disabled="true"
            />
          </div>
        </div>
      </CCol>
    </CRow>
    <div v-for="attribute in attributes">
      <div class="d-flex align-items-center gap-3">
        <h5 style="text-transform: capitalize;">
          {{ attribute.replaceAll("_", " ") }}
          <span v-if="attribute === 'projects'">({{ numOfProjects }})</span>
        </h5>
        <CButton
          v-if="!canEdit && attribute !== 'funding'"
          :disabled="!formId"
          class="ra-icon-button"
          @click="showAttributeCreator(attribute)"
        >
          <Icon name="plus"/>
        </CButton>
        <h5 v-if="attribute === 'projects'" class="ml-auto">Status: {{ projectStatus }}</h5>
      </div>
      <CInputCheckboxGroup
        :disabled="canEdit"
        class="mb-5 radio-group"
        v-if="attribute==='funding'"
        :options="attributeFields.funding.options"
        v-model:checked="attributeFields.funding.value"
        inline
      />
      <tableComponent
        v-else
        default-order-by="account_number"
        :addCollapseCellValue="['description']"
        :addTableClasses="attribute"
        :headings="headings"
        tableDateFormat="DD/MM/YYYY"
        :url="getAttributeUrl(attribute)"
        :columns="[...getAttributeColumns(attribute), 'actions']"
        :filter-exclude-columns="['actions']"
        :sort-exclude-columns="['actions']"
        :key="refreshTable"
      >
        <template
          v-for="column in ['financial_year', 'month']"
          #[column]="{ row }"
        >
          {{ row[column].toString() }}
        </template>
        <template #energy_sales_agreement="{ row }">
          {{row.energy_sales_agreement.join(', ')}}
        </template>
        <template #cashpooling="{ row }">
          {{row.cashpooling === 'Yes' ? "Yes" : row.cashpooling === 'No' ? 'No' : ''}}
        </template>
        <template v-if="!canEdit" #actions="{ row }">
          <div class="d-flex gap-2">
            <CButton
              class="ra-icon-button"
              v-c-tooltip="'Delete form'"
              @click="() => deleteAttribute(attribute, row._id)"
            >
              <Icon name="trash"/>
            </CButton>
            <CButton
              class="ra-icon-button"
              v-c-tooltip="'Edit form'"
              @click="editAttribute(attribute, row)"
            >
              <Icon name="pencil"/>
            </CButton>
          </div>
        </template>
      </tableComponent>
    </div>
    <SideModal
      v-if="showAttributeModal"
      :isVisible="showAttributeModal"
      :wrapper-style="{ width: '30%' }"
      :closeModal="() => showAttributeCreator()"
    >
      <template #modal-header>
        <h3 style="text-transform: capitalize">
          {{ isEdit ? "Edit" : activeModalAttribute === 'projects' ? "Add" : "Create" }}
          {{ activeModalAttribute.replace("_", " ").replace(/s$/, '') }}
        </h3>
      </template>
      <template #modal-content>
        <div class="px-3 py-2">
            <div v-for="formField in attributeFields[activeModalAttribute]">
              <InputComponent
                :key="formField.name"
                :formField="formField"
                @select="projectSelectHandler"
                @remove="removeHandler"
                :incorrectInputValues="incorrectInputValues.includes(formField.name)"
              />
            </div>
          <div class="d-flex justify-content-end gap-4 mt-5">
            <CButton
              color="primary"
              class="ra-button"
              @click="showAttributeModal = false"
            >
              Cancel
            </CButton>
            <CButton
              color="primary"
              class="ra-button"
              @click="handleCreateOrEditAttribute"
            >
              {{ attributeButtonLabels[activeModalAttribute][isEdit ? 1 : 0] }}
            </CButton>
          </div>
        </div>
      </template>
    </SideModal>
  </div>
  <DotsLoader v-else/>
</template>

<script>
import {ENTITY_FORMS_ENDPOINTS} from "@/services/entity_forms";
import {EntityFormService} from "@/services/entity_form";
import {EntityFormsService} from "@/services/entity_forms";
import InputComponent from "@/components/InputComponent";
import {UsersService} from "@/services/users";
import {request} from "@/utils/request";
import {ENTITY_ENDPOINTS} from "@/services/entity";
import {ENTITY_FORM_ENDPOINTS} from "@/services/entity_form";

export default {
  name: "EntityFormEdit",
  components: {InputComponent},
  props: {
    preview: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      projectStatus: null,
      activeAttributeId: null,
      isEdit: false,
      tableLoading: null,
      formId: null,
      refreshTable: 0,
      attributeButtonLabels: {
        projects: ["Create project", "Edit project"],
        bank_accounts: ["Add account", "Edit account"],
        significant_events: ["Add event", "Edit event"]
      },
      attributes: ['projects', 'bank_accounts', 'funding', 'significant_events'],
      activeModalAttribute: null,
      ENTITY_FORMS_ENDPOINTS,
      ENTITY_ENDPOINTS,
      isLoading: false,
      editId: null,
      numOfProjects: 0,
      showAttributeModal: false,
      inputFieldsGroupOptions: [],
      incorrectInputValues: [],
      editedFields: {},
      autoFields: {
        controle_de_gestion: {
          label: "Contrôle de gestion",
          value: ""
        },
        revision: {
          label: "Révision comptable",
          value: ""
        },
        financement: {
          label: "Financement",
          value: ""
        }
      },
      formFields: {
        financial_department: {
          name: "financial_department",
          label: "Financial Department",
          value: "",
          required: true,
          type: "select",
          options: [],
        },
        entity_code: {
          name: "entity_code",
          label: "Entity Code",
          value: "",
          required: true,
          disabled: true,
          searchable: true,
          type: "select",
          options: [],
        },
        name: {
          name: "name",
          label: "Name",
          value: "",
          required: true,
          type: "text",
          fullWidth: true,
        },
        technology: {
          name: "technology",
          label: "technology",
          value: "",
          type: "select",
          options: ["Stockage", "Éolien", "Solaire"],
        }
      },
      attributeFields: {
        projects: {
          eotp: {
            name: "eotp",
            label: "Project code",
            value: "",
            required: true,
            type: "text",
          },
          name: {
            name: "name",
            label: "Project name",
            value: "",
            type: "text",
            required: true,
            fullWidth: true,
          },
          status: {
            name: "status",
            label: "Status",
            value: "",
            required: true,
            type: "select",
            options: ["Construction", "Exploitation"],
          },
          msi_date: {
            name: "msi_date",
            label: "MSI Date",
            value: "",
            type: "date",
            additionalProps:{allowTextInput: false}
          },
          energy_sales_agreement: {
            name: "energy_sales_agreement",
            label: "Energy Sales Agreement",
            value: [],
            type: "multiselect",
            options: [
              {
                label: "Complément de rémunération activé",
                name: "Complément de rémunération activé",
                value: "Complément de rémunération activé",
              },
              {
                label: "Obligation d'achat",
                name: "Obligation d'achat",
                value: "Obligation d'achat",
              },
              {
                label: "Autres",
                name: "Autres",
                value: "Autres",
              }]
          }
        },
        bank_accounts: {
          bank: {
            name: "bank",
            label: "Bank",
            value: "",
            type: "text",
            required: true,
          },
          account_number: {
            name: "account_number",
            label: "Account Number",
            validation: (e) => this.validateAccountNumber(e?.target?.value),
            guidanceText: "Please enter an account number with 27 numerical characters",
            guidanceCondition: (value) => value.toString().length !== 27 || !(new RegExp(/^\d+$/).test(value)),
            value: null,
            type: "text",
            required: true,
          },
          currency: {
            name: "currency",
            label: "Currency",
            value: "",
            required: true,
            type: "select",
            options: ["EUR", "USD"],
          },
          account_type: {
            name: "account_type",
            label: "Account type / Operations",
            value: "",
            type: "text",
          },
          cashpooling: {
            name: "cashpooling",
            label: "Cashpooling (Yes/No)",
            value: "",
            type: "select",
            options: ["Yes", "No"],
          },
        },
        funding: {
          options: ["Compte courant", "Prêt actionnaire", "Emprunt participatif", "Emprunt externe", "Crédit bail", "SWAP"],
          value: [],
        },
        significant_events: {
          financial_year: {
            name: "financial_year",
            label: "Financial year",
            value: "",
            required: true,
            type: "select",
            options: [2020, 2021, 2022, 2023],
          },
          month: {
            name: "month",
            label: "Month",
            value: "",
            required: true,
            type: "select",
            options: Array.from({length: 12}, (_, i) => i + 1),
          },
          description: {
            name: "description",
            label: "Description",
            value: "",
            type: "text",
          },
        },
      }

    }
  },
  watch: {
    "formFields.financial_department.value"() {
      this.fetchEntityCodesOptions();
    },
    "attributeFields.bank_accounts.account_number.value"() {
      this.incorrectInputValues = this.incorrectInputValues.filter(value => value !== 'account_number')
    },
    formId() {
      this.refreshTable++
    },
    refreshTable() {
      this.fetchStatusProject()
    },
  },
  computed: {
    canSaveForm() {
      return Object.entries(this.formFields).every(([_, field]) => {
        return field.required ? !!field.value : true
      })
    },
    headings() {
      let headings = {}
      const res = Object.entries(this.attributeFields).map(([key, value]) => {
        if (key !== "funding") return Object.entries(value).map(([_, value]) => {
          return [value.name, value.label]
        })
        return null
      })
      res.forEach((attribute) => {
        const map = Object.fromEntries(new Map(attribute))
        Object.assign(headings, map)
      })
      return headings
    },
    isDetailsView() {
      return this.$route.name === "Entity Details";
    },
    isPreview(){
      return !!this.$route.query.preview || this.preview
    },
    canEdit() {
      return this.isPreview || !!this.isDetailsView;
    }
  },
  async mounted() {
    this.editId = this.$route.params.entity_id || this.$route.params.id || "create";
    if (this.editId !== "create") {
      await this.populateFields();
    }
    await this.fetchStatusProject()
    await this.fetchFinancialDepartmentOptions();
    await this.fetchEntityCodesOptions();
  },
  methods: {
    validateAccountNumber(value){
      if (!value) return;
      this.attributeFields.bank_accounts.account_number.value = value.replace(/\D/g, '')
    },
    getTitle(){
      return this.editId === 'create' ? "Create" :this.isPreview ? "Preview" : "Edit";
    },
    validateBankAccount() {
      const accountNumberInput = this.attributeFields.bank_accounts.account_number
      if(accountNumberInput.value.length !== 27)
        this.incorrectInputValues.push(accountNumberInput.name);
    },
    async fetchStatusProject() {
      const {data} = await EntityFormService.fetchStatus(this.formId)
      this.projectStatus = data
      this.fetchCountProject()
    },
    async fetchCountProject() {
      const {data} = await EntityFormService.fetchCount(this.formId)
      this.numOfProjects = data
    },
    editAttribute(attributeType, row) {
      const attribute = JSON.parse(JSON.stringify(row));
      this.isEdit = true
      this.activeAttributeId = attribute._id
      Object.keys(this.attributeFields[attributeType]).forEach((key) => {
        this.attributeFields[attributeType][key].value = attribute[key]
      })
      this.activeModalAttribute = attributeType
      this.showAttributeModal = true
    },
    deleteAttribute(attribute, id) {
      EntityFormService.deleteAttribute(attribute, id)
      this.refreshTable++
    },
    getAttributeColumns(attribute) {
      return Object.keys(this.attributeFields[attribute])
    },
    getAttributeUrl(attribute) {
      return ENTITY_FORM_ENDPOINTS.attribute(this.formId, attribute)
    },
    async handleCreateOrEditAttribute() {
      this.incorrectInputValues = [];
      if (this.activeModalAttribute === "bank_accounts") {
        this.validateBankAccount()
      }
      const payload = Object.fromEntries(
        Object.entries(this.attributeFields[this.activeModalAttribute]).map(
          (field) => [field[0], field[1].value ? field[1].value : null]
        )
      );
      if(this.incorrectInputValues.length > 0) return;
      payload.type = this.activeModalAttribute
      payload.form_id = this.formId
      let res
      if (this.isEdit) res = await EntityFormService.editAttribute(payload, this.activeAttributeId);
      else res = await EntityFormService.addAttribute(payload);
      if (res.res.status === 400) return alert("Required data is missing.");
      if (res.res.status === 422) return alert("Project code must be unique!", {color: 'danger'});
      this.isEdit = false;
      this.refreshTable++;
      this.showAttributeModal = false
    },
    removeHandler(item, fieldName) {
      const field = this.attributeFields[this.activeModalAttribute][fieldName];
      field.value.splice(field.value.indexOf(item.value), 1)
    },
    projectSelectHandler(item, fieldName) {
      const field = this.attributeFields[this.activeModalAttribute][fieldName];
      const itemValue = item?.value || item;
      if (!itemValue) return;
      field.value = itemValue;
    },
    showAttributeCreator(attribute) {
      this.activeModalAttribute = attribute;
      this.showAttributeModal = !this.showAttributeModal;
      if (attribute) {
        Object.entries(this.attributeFields[attribute]).forEach((field) => {
          if (field[1].type === "multiselect") field[1].value = [];
          else field[1].value = ""
        })
      } else {
        this.isEdit = false
      }
    },
    selectHandler(form, field) {
      if (field === "entity_code") {
        this.fillAutoFields(form.value);
      }
      this.formFields[field].value = form.value;
      this.editedFields[field] = form.value;
    },
    async fillAutoFields(id) {
      const {data} = await request({
        url: ENTITY_ENDPOINTS.byId(id || this.editId),
        method: "GET",
        query: {
          by_code: true
        }
      });
      Object.keys(this.autoFields).forEach((key) => {
        this.autoFields[key].value = data[key][0];
      })
    },
    async fetchEntityCodesOptions() {
      if (!this.formFields.financial_department.value) return;
      const {data} = await request({
        url: `${ENTITY_ENDPOINTS.main}codes/`,
        query: {
          group: this.formFields.financial_department.value
        }
      });

      this.formFields.entity_code.options = [
        ...data.sort((a, b) => {
          return a.localeCompare(b);
        }),
      ];
      this.formFields.entity_code.disabled = false;
    },
    async fetchFinancialDepartmentOptions() {
      this.formFields.financial_department.options = (
        await UsersService.getRoles()
      ).financial_role.map((role) => {
        return role;
      });
    },
    async populateFields() {
      const form = await EntityFormsService.getForm(this.editId);
      this.$store.commit("set", ["entityName", `${this.isPreview ? "Preview" : "Edit"} Form ${form.name}`]);
      this.formId = form.id
      Object.entries(form).forEach(([key, value]) => {
        if (key !== "id" && key !== "funding") this.formFields[key]["value"] = value;
      });
      this.attributeFields.funding.value = form.funding
      await this.fillAutoFields(this.formFields.entity_code.value);

    },
    async saveForm() {
      this.editedFields['name'] = this.formFields.name.value
      const {data, res} = await EntityFormsService.editOrCreateForm(
        this.editId,
        {...this.editedFields, funding: this.attributeFields.funding.value}
      );
      if (res.status !== 200) {
        return alert(data.detail)
      }
      if (this.editId === 'create') this.$router.push({path:`/entity-id-form/${this.formFields.name.value}`})
    }
  }
}
</script>

<style lang="scss">
.radio-group .form-check label {
  font-size: 16px !important;
  color: #0071CEFF !important;
}

.form-inline {
  gap: 10px;
}

.auto-form-wrapper {
  width: 100%;
  display: grid;
  grid-template-columns: 25% 75%;
  grid-gap: 10px;
  &:last-child {
    min-width: 100%
  }
}

.entity-form {
  .VueTables {
    background: transparent;
  }
  .form-check > div {
    display: flex;
  }
}
</style>
